import { API_BASE_URL } from '@/config/constants'
import { AUTH_TOKEN_KEY } from '@/config/constants'
import apiService from '../api'

export interface DocumentListItem {
  id: string
  name: string
  type?: string | null
  url: string
  uploadedAt?: string
}

interface DocumentsListResponse {
  success: boolean
  data: { documents: DocumentListItem[]; meta?: unknown }
}

/**
 * Fetch documents linked to an entity (e.g. contract, shipment) for display in report row popup.
 */
export async function getDocumentsByEntity(
  relatedEntityType: string,
  relatedEntityId: string
): Promise<DocumentListItem[]> {
  const params = new URLSearchParams({ relatedEntityType, relatedEntityId, limit: '50' })
  const res = await apiService.get<DocumentsListResponse>(`/documents?${params.toString()}`)
  return res?.data?.documents ?? []
}

/**
 * Upload a document linked to a shipment.
 * Backend accepts PDF only for the global documents upload.
 * Uses fetch so we don't set Content-Type (browser sets multipart/form-data with boundary).
 */
interface DocumentUrlResponse {
  success: boolean
  data: { url: string }
}

const documentRequestConfig = { suppressGlobalError: true as const }

/** Resolve a viewable URL (signed S3 URL or backend-hosted file URL). */
export async function getDocumentViewUrl(documentId: string): Promise<string> {
  const response = await apiService.get<DocumentUrlResponse>(
    `/documents/${documentId}/url`,
    documentRequestConfig
  )
  const url = response?.data?.url
  if (!url) throw new Error('Document URL not available')
  return url
}

/** Open URL in a new tab; anchor fallback when popup blockers return null from window.open. */
function openInNewTab(url: string): void {
  const tab = window.open(url, '_blank')
  if (tab) return

  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/** Fetch document bytes (S3 signed URL or backend download endpoint). */
async function fetchDocumentBlob(documentId: string): Promise<Blob> {
  const url = await getDocumentViewUrl(documentId)

  if (/^https?:\/\//i.test(url)) {
    const response = await fetch(url)
    if (!response.ok) throw new Error('Failed to fetch document')
    const blob = await response.blob()
    if (blob.type?.includes('application/json')) {
      const text = await blob.text()
      const parsed = JSON.parse(text) as { error?: { message?: string } }
      throw new Error(parsed.error?.message || 'Failed to load document')
    }
    return blob
  }

  return fetchLocalDocumentBlob(documentId)
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename || 'document.pdf'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

/** Fetch document bytes for local files served by the backend. */
async function fetchLocalDocumentBlob(documentId: string): Promise<Blob> {
  const response = await apiService.instance.get(`/documents/${documentId}/download`, {
    responseType: 'blob',
    suppressGlobalError: true,
  })
  const blob = response.data as Blob
  if (blob.type?.includes('application/json')) {
    const text = await blob.text()
    const parsed = JSON.parse(text) as { error?: { message?: string } }
    throw new Error(parsed.error?.message || 'Failed to load document')
  }
  return blob
}

/** Open a document in a new browser tab for viewing. */
export async function openDocumentById(documentId: string): Promise<void> {
  const url = await getDocumentViewUrl(documentId)

  // Signed S3 / absolute URLs: open directly. Do not fall back to /download —
  // window.open with noopener often returns null even when the tab opened.
  if (/^https?:\/\//i.test(url)) {
    openInNewTab(url)
    return
  }

  // Relative backend path — stream via authenticated download endpoint.
  const blob = await fetchLocalDocumentBlob(documentId)
  const objectUrl = URL.createObjectURL(blob)
  openInNewTab(objectUrl)
  setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000)
}

/** Download a document file by ID. */
export async function downloadDocumentById(documentId: string, filename: string): Promise<void> {
  const blob = await fetchDocumentBlob(documentId)
  triggerBlobDownload(blob, filename || 'document.pdf')
}

/** Download multiple documents sequentially (browser-friendly). */
export async function downloadMultipleDocuments(
  documents: Array<{ id: string; name: string }>
): Promise<void> {
  for (let i = 0; i < documents.length; i++) {
    const doc = documents[i]
    const blob = await fetchDocumentBlob(doc.id)
    triggerBlobDownload(blob, doc.name || `document-${i + 1}.pdf`)
    if (i < documents.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 400))
    }
  }
}

export async function uploadShipmentDocument(shipmentId: string, file: File): Promise<void> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('relatedEntityType', 'shipment')
  formData.append('relatedEntityId', shipmentId)

  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(AUTH_TOKEN_KEY) : null
  const headers: HeadersInit = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_BASE_URL}/documents/upload`, {
    method: 'POST',
    headers,
    body: formData,
  })

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}))
    const msg = (errBody as { error?: { message?: string } })?.error?.message ?? res.statusText ?? 'Upload failed'
    throw new Error(msg)
  }
}

/** Upload multiple PDFs linked to a shipment (one request per file). */
export async function uploadShipmentDocuments(shipmentId: string, files: File[]): Promise<void> {
  for (const file of files) {
    await uploadShipmentDocument(shipmentId, file)
  }
}
